import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import FeatherIcon from "feather-icons-react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Button,
  FormGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import BreadCrumb from "../../../components/common/BreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/common/Loader";
import 'react-toastify/dist/ReactToastify.css';

// Import actions
import { getClientUserList } from "../../../slices/veloxity/client/ClientUserListReducer";
import { createClientUser } from "../../../slices/veloxity/client/ClientUserCreateReducer";
import { updateClientUser } from "../../../slices/veloxity/client/ClientUserUpdateReducer";
import { deleteClientUser } from "../../../slices/veloxity/client/ClientUserDeleteReducer";

const CompanyUser = () => {
  const { clientId } = useParams();
  const dispatch = useDispatch();

  const { loading, payload: users, error } = useSelector(state => state.clientUserList);
  const createState = useSelector(state => state.createClientUser);
  const updateState = useSelector(state => state.updateClientUser);
  const deleteState = useSelector(state => state.deleteClientUser);

  
  useEffect(() => {
    const isCreateFinished = !createState.loading && !createState.error;
    const isUpdateFinished = !updateState.loading && !updateState.error;
    const isDeleteFinished = !deleteState.loading && !deleteState.error;

    if (clientId && (isCreateFinished || isUpdateFinished || isDeleteFinished)) {
        dispatch(getClientUserList(clientId));
    }
  }, [clientId, createState, updateState, deleteState, dispatch]);
  
  

  const [user, setUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [newUser, setNewUser] = useState({
    name: "",
    surname: "",
    email: ""
  });

  const onClickData = (user) => {
    setUser(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = () => {
    if (user) {
      dispatch(deleteClientUser(user.userId));
      setDeleteModal(false);
    }
    setTimeout(() => {
      dispatch(getClientUserList(clientId));
    }, 1000); 
  };
  

  const handleAddNewUser = () => {
    dispatch(createClientUser({ clientId, clientData: newUser }));
    setAddModal(false);
  };

  const handleEditUser = () => {
    dispatch(updateClientUser({ userId: user.userId, clientData: user }));
    setEditModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter users based on search term
  const filteredUsers = users?.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to get initials
  const getInitials = (name, surname) => {
    return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
  };

  // Column setup for users table
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Surname",
        accessorKey: "surname",
        enableColumnFilter: false,
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: "Actions",
        cell: (cellProps) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item" title="Edit">
                <Link className="edit-item-btn" to="#"
                  onClick={() => { const userData = cellProps.row.original; setUser(userData); setEditModal(true); }}
                >
                  <i className="ri-pencil-fill align-bottom text-muted"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Delete">
                <Link
                  className="remove-item-btn"
                  onClick={() => { const userData = cellProps.row.original; onClickData(userData); }}
                  to="#"
                >
                  <i className="ri-delete-bin-fill align-bottom text-muted"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} backdrop="static">
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Delete User</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this user?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={() => handleDeleteUser()}>Delete</Button>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={addModal} toggle={() => setAddModal(!addModal)}>
        <ModalHeader toggle={() => setAddModal(!addModal)}>Add New User</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="name">Name</Label>
            <Input type="text" name="name" id="name" value={newUser.name} onChange={handleInputChange} />
          </FormGroup>
          <FormGroup>
            <Label for="surname">Surname</Label>
            <Input type="text" name="surname" id="surname" value={newUser.surname} onChange={handleInputChange} />
          </FormGroup>
          <FormGroup>
            <Label for="email">Email</Label>
            <Input type="text" name="email" id="email" value={newUser.email} onChange={handleInputChange} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddNewUser}>Add User</Button>{' '}
          <Button color="secondary" onClick={() => setAddModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)}>
        <ModalHeader toggle={() => setEditModal(!editModal)}>Edit User</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="name">Name</Label>
            <Input type="text" name="name" id="name" value={user?.name || ''} onChange={handleEditInputChange} />
          </FormGroup>
          <FormGroup>
            <Label for="surname">Surname</Label>
            <Input type="text" name="surname" id="surname" value={user?.surname || ''} onChange={handleEditInputChange} />
          </FormGroup>
          <FormGroup>
            <Label for="email">Email</Label>
            <Input type="text" name="email" id="email" value={user?.email || ''} onChange={handleEditInputChange} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEditUser}>Update User</Button>{' '}
          <Button color="secondary" onClick={() => setEditModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <div >
          <Row className="g-4 mb-3">
            <div className="col-sm-auto">
              <div>
                <Button color="success" onClick={() => setAddModal(true)}>
                  <i className="ri-add-line align-bottom me-1"></i> New User
                </Button>
              </div>
            </div>
            <div className="col-sm-3 ms-auto">
              <div className="d-flex justify-content-sm-end gap-2">
                <div className="search-box ms-2 col-sm-7">
                  <Input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search..." 
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <i className="ri-search-line search-icon"></i>
                </div>
              </div>
            </div>
          </Row>

          <Row>
            {loading ? (
              <div style={{paddingTop: '15%', paddingBottom: '15%'}}>
                <Loader error={error} />
              </div>
            ) : error ? (
              <div className="d-flex justify-content-center mx-2 mt-2">
                <i className="mdi mdi-alert-circle-outline fs-24 text-danger me-2"></i>
                <span className="text-danger">{error}</span>
              </div>
            ) : (
              Array.isArray(users) && filteredUsers.map((item, index) => (
                <Col xxl={3} sm={6} className="project-card" key={index}>
                  <Card className="card-height-100">
                    <CardBody>
                      <div className="d-flex flex-column h-100">
                        <div className="d-flex align-items-center mb-2">
                          <div className="avatar-sm me-3">
                            <span className="avatar-title rounded-circle bg-light text-primary fs-22">
                              {getInitials(item.name, item.surname)}
                            </span>
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="mb-1 fs-15">{item.name} {item.surname}</h5>
                            <p className="text-muted text-truncate-two-lines mb-0">{item.email}</p>
                          </div>
                          <div className="flex-shrink-0">
                            <UncontrolledDropdown direction='start'>
                              <DropdownToggle tag="button" className="btn btn-link text-muted p-1 mt-n2 py-0 text-decoration-none fs-15">
                                <FeatherIcon icon="more-horizontal" className="icon-sm" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem href="#" onClick={() => { setUser(item); setEditModal(true); }}>
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> Edit
                                </DropdownItem>
                                <div className="dropdown-divider"></div>
                                <DropdownItem href="#" onClick={() => onClickData(item)}> 
                                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> Remove
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
            )}
          </Row>

      </div>
    </React.Fragment>
  );
};

export default CompanyUser;
